import { useState } from 'react';
import { Plus, Trash2, ExternalLink, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const input = 'px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm w-full';
const FIELDS = [
  ['challenge_rules', 'Challenge rules'], ['profit_target', 'Profit target'], ['drawdown', 'Drawdown / loss limits'],
  ['payout_split', 'Payout split'], ['payout_schedule', 'Payout schedule & conditions'],
];
const today = () => new Date().toISOString().slice(0, 10);

const DigestBox = ({ f, authHeader }) => {
  const [msg, setMsg] = useState('');
  const refresh = async () => {
    const res = await fetch(`${API_URL}/api/admin/support/prop-firms/${f.id}/refresh`, { method: 'POST', headers: authHeader });
    setMsg(res.ok ? 'Reading the official site (1-3 min). Reopen this panel to see the update.' : 'Add official domains and save first.');
  };
  return (
    <div className="rounded-lg bg-black/20 p-2" data-testid={`prop-firm-digest-${f.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-white/50 text-xs">Official site digest {f.digest_checked_at ? `(as of ${f.digest_checked_at}, refreshed daily)` : '(not read yet)'}</span>
        <button type="button" onClick={refresh} className="text-xs text-[#a78bfa] flex items-center gap-1" data-testid={`prop-firm-refresh-${f.id}`}><RefreshCw size={12} /> Refresh from official site</button>
      </div>
      {msg && <p className="text-[#a78bfa] text-xs mt-1">{msg}</p>}
      {f.official_digest && <pre className="mt-2 max-h-48 overflow-y-auto whitespace-pre-wrap text-white/60 text-xs font-sans">{f.official_digest}</pre>}
    </div>
  );
};

const FirmCard = ({ f, onChange, onRemove, authHeader }) => (
  <div className="rounded-lg border border-white/10 p-3 space-y-2" data-testid={`prop-firm-${f.id}`}>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
      <input className={`${input} md:col-span-3`} placeholder="Firm name" value={f.name} onChange={(e) => onChange({ name: e.target.value })} data-testid={`prop-firm-name-${f.id}`} />
      <select className={`${input} md:col-span-2`} value={f.market} onChange={(e) => onChange({ market: e.target.value })} data-testid={`prop-firm-market-${f.id}`}>
        {['CFD', 'Futures'].map((m) => <option key={m} value={m} className="bg-[#0a0a12]">{m}</option>)}
      </select>
      <input className={`${input} md:col-span-3`} placeholder="Program" value={f.program} onChange={(e) => onChange({ program: e.target.value })} />
      <div className="md:col-span-4 flex items-center justify-end gap-2">
        <span className="text-white/40 text-xs">{f.approved ? `checked ${f.last_checked}` : 'draft'}</span>
        <button type="button" data-testid={`prop-firm-approve-${f.id}`}
          onClick={() => onChange(f.approved ? { approved: false } : { approved: true, last_checked: today() })}
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${f.approved ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-white/10 text-white/50'}`}>
          {f.approved ? 'Approved' : 'Not approved'}
        </button>
        <button type="button" onClick={onRemove} data-testid={`prop-firm-remove-${f.id}`}><Trash2 size={14} className="text-white/40 hover:text-red-400" /></button>
      </div>
    </div>
    {FIELDS.map(([key, label]) => (
      <label key={key} className="block text-xs text-white/40">{label}
        <textarea rows={2} className={`${input} mt-1`} value={f[key] || ''} onChange={(e) => onChange({ [key]: e.target.value })} data-testid={`prop-firm-${key}-${f.id}`} />
      </label>
    ))}
    <DigestBox f={f} authHeader={authHeader} />
    <div className="flex flex-wrap items-center gap-2">
      <input className={`${input} md:w-1/2`} placeholder="Official domains for live lookup, e.g. lucidtrading.com" value={(f.domains || []).join(', ')}
        onChange={(e) => onChange({ domains: e.target.value.split(',').map((d) => d.trim().toLowerCase()).filter(Boolean) })} data-testid={`prop-firm-domains-${f.id}`} />
      <button type="button" onClick={() => onChange({ recommended: !f.recommended })} data-testid={`prop-firm-recommend-${f.id}`}
        className={`px-2.5 py-1 rounded-full text-xs font-medium ${f.recommended ? 'bg-[#a78bfa]/20 text-[#a78bfa]' : 'bg-white/10 text-white/50'}`}>
        {f.recommended ? 'We recommend this firm' : 'Not recommended'}
      </button>
    </div>
    <div className="flex items-center gap-2">
      <input className={input} placeholder="Official rules URL" value={f.official_url || ''} onChange={(e) => onChange({ official_url: e.target.value })} data-testid={`prop-firm-url-${f.id}`} />
      {f.official_url && <a href={f.official_url} target="_blank" rel="noopener noreferrer" className="text-[#a78bfa]" data-testid={`prop-firm-open-${f.id}`}><ExternalLink size={14} /></a>}
    </div>
  </div>
);

export const PropFirmsEditor = ({ firms = [], onChange, authHeader }) => {
  const update = (i, patch) => onChange(firms.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const add = () => onChange([...firms, { id: `firm-${Date.now()}`, name: '', market: 'CFD', program: '', approved: false, last_checked: '' }]);
  return (
    <div data-testid="prop-firms-editor">
      <div className="flex items-center justify-between mb-1"><h3 className="text-white font-semibold">Prop firm knowledge base</h3>
        <button type="button" onClick={add} className="text-xs text-[#a78bfa] flex items-center gap-1" data-testid="prop-firm-add"><Plus size={12} /> Add firm</button></div>
      <p className="text-white/40 text-xs mb-3">Approved firms are answered from these fields. For anything else the agent checks live, searching only the firm's official domains, and always tells visitors to confirm on the firm's site. "We recommend" firms are the only ones the agent suggests when asked which firm to use.</p>
      <div className="space-y-3">{firms.map((f, i) => <FirmCard key={f.id} f={f} authHeader={authHeader} onChange={(p) => update(i, p)} onRemove={() => onChange(firms.filter((_, j) => j !== i))} />)}</div>
    </div>
  );
};
